import { supabase } from './client';
import { Database } from './types';

type SparePartType = Database['public']['Tables']['spare_part_types']['Row'];
type MainInventory = Database['public']['Tables']['main_inventory']['Row'];
type BranchInventory = Database['public']['Tables']['branch_inventory']['Row'];
type InventoryTransfer = Database['public']['Tables']['inventory_transfers']['Row'];
type BikeSpareAssignment = Database['public']['Tables']['bike_spare_assignments']['Row'];

// Tipos de repuestos
export const getSparePartTypes = async () => {
  const { data, error } = await supabase
    .from('spare_part_types')
    .select('*')
    .order('name');
  
  if (error) throw error;
  return data;
};

export const getBikeRepairHistory = async (bikeId: string) => {
    if (!bikeId) return [];
  
    const { data, error } = await supabase
      .from('bike_spare_assignments')
      .select(`
        id,
        assignment_date,
        quantity_used,
        notes,
        spare_part_type:spare_part_types(name),
        profile:profiles!assigned_by(full_name)
      `)
      .eq('bike_id', bikeId)
      .order('assignment_date', { ascending: false });
  
    if (error) {
      console.error('Error fetching bike repair history:', error);
      throw error;
    }
    
    return data;
  };

export const createSparePartType = async (sparePartType: Omit<SparePartType, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('spare_part_types')
    .insert(sparePartType)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Inventario principal
export const getMainInventory = async () => {
  const { data, error } = await supabase
    .from('main_inventory')
    .select(`
      *,
      spare_part_type:spare_part_types(*)
    `)
    .order('updated_at', { ascending: false });
  
  if (error) throw error;
  return data;
};

export const updateMainInventoryQuantity = async (id: string, quantity: number) => {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('Usuario no autenticado');

  const { data, error } = await supabase
    .from('main_inventory')
    .update({ 
      quantity,
      updated_by: userData.user.id
    })
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const addToMainInventory = async (sparePartTypeId: string, quantity: number) => {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('Usuario no autenticado');

  const { data, error } = await supabase
    .from('main_inventory')
    .insert({
      spare_part_type_id: sparePartTypeId,
      quantity,
      created_by: userData.user.id,
      updated_by: userData.user.id
    })
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Inventario de sucursales
export const getBranchInventory = async (branchId?: string) => {
  let query = supabase
    .from('branch_inventory')
    .select(`
      *,
      spare_part_type:spare_part_types(*),
      branch:branches(*),
      assigned_to_profile:profiles!assigned_to(full_name)
    `);

  if (branchId) {
    query = query.eq('branch_id', branchId);
  }

  const { data, error } = await query.order('updated_at', { ascending: false });
  
  if (error) throw error;
  return data;
};

// Transferencias de inventario
export const transferToBranch = async (
  sparePartTypeId: string,
  toBranchId: string,
  quantity: number,
  notes?: string
) => {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('Usuario no autenticado');

  // 1. Verificar si la sucursal tiene un administrador
  const { data: branchAdmins } = await supabase
    .from('profiles')
    .select('id')
    .eq('branch_id', toBranchId)
    .eq('role', 'branch_admin');

  const hasBranchAdmin = branchAdmins && branchAdmins.length > 0;
  let assignedToId: string | null = null;

  // 2. Lógica: Si NO hay admin -> Asignar a un operador automáticamente
  // Si HAY admin -> Se queda en null (el admin debe asignarlo manualmente)
  if (!hasBranchAdmin) {
     const { data: operators } = await supabase
        .from('profiles')
        .select('id')
        .eq('branch_id', toBranchId)
        .eq('role', 'operator')
        .limit(1)
        .maybeSingle();
      
      if (operators) {
        assignedToId = operators.id;
      }
  }

  // Primero verificar que hay suficiente stock en el inventario principal
  const { data: mainStock } = await supabase
    .from('main_inventory')
    .select('quantity')
    .eq('spare_part_type_id', sparePartTypeId)
    .single();

  if (!mainStock || mainStock.quantity < quantity) {
    throw new Error('Stock insuficiente en inventario principal');
  }

  // Crear la transferencia
  const { data: transfer, error: transferError } = await supabase
    .from('inventory_transfers')
    .insert({
      spare_part_type_id: sparePartTypeId,
      to_branch_id: toBranchId,
      quantity,
      transferred_by: userData.user.id,
      notes
    })
    .select()
    .single();

  if (transferError) throw transferError;

  // Actualizar inventario principal (restar)
  const { error: mainUpdateError } = await supabase
    .from('main_inventory')
    .update({ 
      quantity: mainStock.quantity - quantity,
      updated_by: userData.user.id
    })
    .eq('spare_part_type_id', sparePartTypeId);

  if (mainUpdateError) throw mainUpdateError;

  // Actualizar o crear inventario de sucursal (sumar)
  const { data: branchStock } = await supabase
    .from('branch_inventory')
    .select('quantity, assigned_to')
    .eq('branch_id', toBranchId)
    .eq('spare_part_type_id', sparePartTypeId)
    .maybeSingle();

  if (branchStock) {
    // Actualizar existente
    // Si NO hay admin, aseguramos que esté asignado (si ya tenía asignación, se mantiene o se actualiza)
    // Si HAY admin, forzamos assigned_to a NULL (el admin toma control del nuevo stock total)
    const newAssignedTo = hasBranchAdmin ? null : (branchStock.assigned_to || assignedToId);

    const { error: branchUpdateError } = await supabase
      .from('branch_inventory')
      .update({ 
        quantity: branchStock.quantity + quantity,
        updated_by: userData.user.id,
        assigned_to: newAssignedTo
      })
      .eq('branch_id', toBranchId)
      .eq('spare_part_type_id', sparePartTypeId);

    if (branchUpdateError) throw branchUpdateError;
  } else {
    // Crear nuevo
    const { error: branchInsertError } = await supabase
      .from('branch_inventory')
      .insert({
        branch_id: toBranchId,
        spare_part_type_id: sparePartTypeId,
        quantity,
        created_by: userData.user.id,
        updated_by: userData.user.id,
        assigned_to: assignedToId
      });

    if (branchInsertError) throw branchInsertError;
  }

  return transfer;
};

export const getInventoryTransfers = async (branchId?: string) => {
  let query = supabase
    .from('inventory_transfers')
    .select(`
      *,
      spare_part_type:spare_part_types(*),
      branch:branches(*),
      transferred_by_profile:profiles!transferred_by(*)
    `);

  if (branchId) {
    query = query.eq('to_branch_id', branchId);
  }

  const { data, error } = await query.order('transfer_date', { ascending: false });
  
  if (error) throw error;
  return data;
};

// Funciones para el Branch Admin

export const addToBranchInventory = async (
  branchId: string,
  sparePartTypeId: string,
  quantity: number
) => {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('Usuario no autenticado');

  // Verificar si ya existe el ítem en la sucursal
  const { data: existingItem } = await supabase
    .from('branch_inventory')
    .select('quantity')
    .eq('branch_id', branchId)
    .eq('spare_part_type_id', sparePartTypeId)
    .maybeSingle();

  if (existingItem) {
    const { data, error } = await supabase
      .from('branch_inventory')
      .update({
        quantity: existingItem.quantity + quantity,
        updated_by: userData.user.id
      })
      .eq('branch_id', branchId)
      .eq('spare_part_type_id', sparePartTypeId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase
      .from('branch_inventory')
      .insert({
        branch_id: branchId,
        spare_part_type_id: sparePartTypeId,
        quantity,
        created_by: userData.user.id,
        updated_by: userData.user.id,
        assigned_to: null // Se crea sin asignar por defecto para que el Admin decida
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

export const assignBranchInventoryItem = async (
  inventoryId: string,
  assignedToUserId: string | null
) => {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('Usuario no autenticado');

  const { data, error } = await supabase
    .from('branch_inventory')
    .update({
      assigned_to: assignedToUserId,
      updated_by: userData.user.id
    })
    .eq('id', inventoryId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Asignaciones a bicicletas
export const assignSpareToBike = async (
  bikeId: string,
  sparePartTypeId: string,
  branchId: string,
  quantityUsed: number,
  notes?: string
) => {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('Usuario no autenticado');

  // Verificar stock en sucursal
  const { data: branchStock } = await supabase
    .from('branch_inventory')
    .select('quantity')
    .eq('branch_id', branchId)
    .eq('spare_part_type_id', sparePartTypeId)
    .single();

  if (!branchStock || branchStock.quantity < quantityUsed) {
    throw new Error('Stock insuficiente en inventario de sucursal');
  }

  // Crear la asignación
  const { data: assignment, error: assignmentError } = await supabase
    .from('bike_spare_assignments')
    .insert({
      bike_id: bikeId,
      spare_part_type_id: sparePartTypeId,
      branch_id: branchId,
      quantity_used: quantityUsed,
      assigned_by: userData.user.id,
      notes
    })
    .select()
    .single();

  if (assignmentError) throw assignmentError;

  // Actualizar inventario de sucursal (restar)
  const { error: updateError } = await supabase
    .from('branch_inventory')
    .update({ 
      quantity: branchStock.quantity - quantityUsed,
      updated_by: userData.user.id
    })
    .eq('branch_id', branchId)
    .eq('spare_part_type_id', sparePartTypeId);

  if (updateError) throw updateError;

  return assignment;
};

export const getBikeSpareAssignments = async (branchId?: string, bikeId?: string) => {
  let query = supabase
    .from('bike_spare_assignments')
    .select(`
      *,
      spare_part_type:spare_part_types(*),
      bike:bikes(*),
      assigned_by_profile:profiles!assigned_by(*)
    `);

  if (branchId) {
    query = query.eq('branch_id', branchId);
  }

  if (bikeId) {
    query = query.eq('bike_id', bikeId);
  }

  const { data, error } = await query.order('assignment_date', { ascending: false });
  
  if (error) throw error;
  return data;
};
